// PWA shell (2026-10-02): service worker registration + "Add to Home
// Screen" onboarding for the field workspaces.
//
// HTTPS assumption: camera, geolocation, compass and service workers all
// need a secure context. Deployments are reached through the Cloudflare
// tunnel (HTTPS); http://127.0.0.1 also counts as secure for local dev. On a
// plain-http LAN address none of this works, and we say so.

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: string }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;

// --- keeping the page and the service worker on the same build (2026-10-08) --
// The wraptest.nz phone ran an old cached build against a new backend (see
// sw.js v3). The page now knows its own build (<meta name="wrap-build">,
// stamped by vite.config.ts) and:
//  - after boot, fetches /version.json past every cache; on a different
//    build it asks the browser to update the worker (registration.update());
//  - when a new worker activates ("wrap-sw-activated" from sw.js) for a
//    build other than its own, reloads - at once while the app is still
//    booting, else it offers a "Reload" banner (a crew member may be
//    half-way through a capture form);
//  - a lazy chunk that fails to load (vite:preloadError, or a failed
//    import() reported through reloadForStaleChunk) reloads once, after
//    first letting a newer worker install. A sessionStorage guard stops a
//    reload loop: at most one automatic reload per 60 s.

const RELOAD_GUARD_KEY = "wrap.swReloadGuard";
const RELOAD_GUARD_MS = 60_000;
let booted = false;

/** The build this page was built as (null for an unbuilt dev page). */
export function pageBuild(): string | null {
  const v = document.querySelector<HTMLMetaElement>('meta[name="wrap-build"]')?.content ?? "";
  return /^[0-9a-f]{12}$/.test(v) ? v : null;
}

/** main.ts: the app finished booting (a new worker then offers, not forces, a reload). */
export function markBooted(): void {
  booted = true;
}

function reloadOnce(reason: string): boolean {
  let last: { at: number; reason: string } | null = null;
  try {
    last = JSON.parse(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? "null");
  } catch {
    /* storage blocked */
  }
  if (last && Date.now() - last.at < RELOAD_GUARD_MS) return false;
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, JSON.stringify({ at: Date.now(), reason }));
  } catch {
    /* storage blocked: still reload once */
  }
  window.location.reload();
  return true;
}

function showUpdateBanner(): void {
  if (document.getElementById("wrap-update-banner")) return;
  const bar = document.createElement("div");
  bar.id = "wrap-update-banner";
  bar.setAttribute("role", "status");
  bar.className = "wrap-update-banner";
  bar.style.cssText =
    "position:fixed;left:50%;transform:translateX(-50%);bottom:calc(12px + env(safe-area-inset-bottom));z-index:4000;" +
    "display:flex;gap:10px;align-items:center;background:#1f3a5f;color:#fff;padding:10px 14px;border-radius:10px;" +
    "box-shadow:0 4px 16px rgba(0,0,0,.35);font:14px system-ui,sans-serif;max-width:calc(100vw - 32px)";
  const text = document.createElement("span");
  text.textContent = "A new version of WRAP is ready.";
  const btn = document.createElement("button");
  btn.type = "button";
  btn.textContent = "Reload";
  btn.addEventListener("click", () => window.location.reload());
  const later = document.createElement("button");
  later.type = "button";
  later.className = "wrap-update-later";
  btn.style.cssText = "background:#fff;color:#1f3a5f;border:0;border-radius:6px;padding:6px 12px;font-weight:600";
  later.style.cssText = "background:transparent;color:#fff;border:1px solid rgba(255,255,255,.5);border-radius:6px;padding:6px 10px";
  later.textContent = "Later";
  later.addEventListener("click", () => bar.remove());
  bar.append(text, btn, later);
  document.body.append(bar);
}

function onWorkerActivated(build: string | null): void {
  const mine = pageBuild();
  if (!build || !mine || build === mine) return;
  if (!booted) reloadOnce(`worker ${build} activated during boot`);
  else showUpdateBanner();
}

async function waitForNewWorker(reg: ServiceWorkerRegistration, ms: number): Promise<void> {
  if (!reg.installing && !reg.waiting) return;
  await new Promise<void>((resolve) => {
    const t = window.setTimeout(resolve, ms);
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      window.clearTimeout(t);
      resolve();
    }, { once: true });
  });
}

/** A lazy chunk (an old build's, removed by a deploy) failed to load: let a
 * newer worker install if there is one, then reload once. false = the loop
 * guard refused (the caller shows its own message). */
export async function reloadForStaleChunk(): Promise<boolean> {
  try {
    navigator.serviceWorker?.controller?.postMessage("clear-runtime-assets");
    const reg = await navigator.serviceWorker?.getRegistration();
    if (reg) {
      await reg.update().catch(() => undefined);
      await waitForNewWorker(reg, 15000);
    }
  } catch {
    /* no worker: a plain reload */
  }
  return reloadOnce("stale chunk");
}

/** /version.json (never cached, never through the worker) vs this page. */
export async function checkBuild(): Promise<void> {
  const mine = pageBuild();
  if (!mine || !navigator.onLine) return;
  let server: string | null = null;
  try {
    const res = await fetch("/version.json", { cache: "no-store", credentials: "same-origin" });
    if (!res.ok) return;
    server = ((await res.json()) as { build?: string }).build ?? null;
  } catch {
    return; // offline / flaky: try again next time
  }
  if (!server || server === mine) return;
  const reg = await navigator.serviceWorker?.getRegistration().catch(() => undefined);
  // the new worker's activation message then reloads (booting) or offers a reload
  if (reg) await reg.update().catch(() => undefined);
  else if (!booted) reloadOnce(`server build ${server}`);
  else showUpdateBanner();
}

export function registerServiceWorker(): void {
  // a lazy chunk of a build the server no longer has (vite's dynamic-import preload)
  // (no preventDefault: that makes vite resolve the import with undefined
  // instead of rejecting, and the caller can't tell it failed; the guard in
  // reloadOnce stops this and the caller's own recovery reloading twice)
  window.addEventListener("vite:preloadError", () => {
    void reloadForStaleChunk();
  });
  if (!("serviceWorker" in navigator) || !window.isSecureContext) return;
  navigator.serviceWorker.addEventListener("message", (e) => {
    const d = e.data as { type?: string; build?: string | null } | null;
    if (d?.type === "wrap-sw-activated") onWorkerActivated(d.build ?? null);
  });
  const register = () => {
    navigator.serviceWorker
      .register("/sw.js", { scope: "/", updateViaCache: "none" })
      .then(() => checkBuild())
      .catch((e) => console.warn("service worker:", e));
  };
  if (document.readyState === "complete") register();
  else window.addEventListener("load", register);
  // back online / app shown again: a deploy may have happened meanwhile
  window.addEventListener("online", () => void checkBuild());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void checkBuild();
  });
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    document.dispatchEvent(new CustomEvent("field-install-available"));
  });
}

export function isStandalone(): boolean {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

export function canPromptInstall(): boolean {
  return deferredPrompt !== null;
}

export async function promptInstall(): Promise<boolean> {
  if (!deferredPrompt) return false;
  await deferredPrompt.prompt();
  const { outcome } = await deferredPrompt.userChoice;
  deferredPrompt = null;
  return outcome === "accepted";
}

const DISMISS_KEY = "wcc.field.installDismissed";

/** The onboarding card's content (null = nothing to say: already
 * installed, or dismissed). */
export function installAdvice(ignoreDismissed = false): { title: string; body: string; canPrompt: boolean } | null {
  if (isStandalone()) return null;
  try {
    if (!ignoreDismissed && localStorage.getItem(DISMISS_KEY)) return null;
  } catch {
    /* storage blocked */
  }
  if (!window.isSecureContext) {
    return {
      title: "Open over HTTPS",
      body: "Camera, location and compass only work over HTTPS. Use the tunnel address (https://…), not a plain http:// LAN address.",
      canPrompt: false,
    };
  }
  if (isIOS()) {
    return {
      title: "Add WRAP to your Home Screen",
      body: "In Safari tap the Share button, then “Add to Home Screen”. Launch WRAP from the icon: queued photos are then kept safely until they upload.",
      canPrompt: false,
    };
  }
  return {
    title: "Install WRAP on this phone",
    body: canPromptInstall()
      ? "Install the app so it opens full-screen and keeps queued photos safely until they upload."
      : "In Chrome open the ⋮ menu and choose “Install app” / “Add to Home screen”.",
    canPrompt: canPromptInstall(),
  };
}

/** Bring the install card back after "Not now". */
export function resetInstallAdvice(): void {
  try {
    localStorage.removeItem(DISMISS_KEY);
  } catch {
    /* storage blocked */
  }
}

export function dismissInstallAdvice(): void {
  try {
    localStorage.setItem(DISMISS_KEY, "1");
  } catch {
    /* storage blocked */
  }
}
